/**
 * Grouping logic abstraction for TaskGrid
 * Handles different grouping types: County, Municipality, User
 */

/**
 * Maps selected grid rows to /load_tasks_by_selection params.
 * viewMode alone is wrong when groupingType shows municipalities at county level.
 */
export const getSelectionSearchConfig = (viewMode, groupingType) => {
  if (viewMode === 'municipality' || groupingType === 'Municipality') {
    return {
      field: 'muniCodes',
      extractValue: (row) => row.muniCode || row.MuniCode,
    };
  }
  if (groupingType === 'User') {
    return {
      field: 'userId',
      extractValue: (row) => row.userId || row.UserId || row.Assignee,
      perValue: true,
    };
  }
  return {
    field: 'countyCodes',
    extractValue: (row) => row.countyCode || row.CountyCode,
  };
};

/**
 * Determines the effective view mode based on groupingType and current viewMode
 * @param {string} viewMode - Current view mode ('county' | 'municipality' | 'tasks')
 * @param {string} groupingType - Current grouping type ('County' | 'Municipality' | 'User')
 * @returns {string} Effective view mode
 */
export const getEffectiveViewMode = (viewMode, groupingType) => {
  // Keep viewMode as 'county' when grouping by Municipality to show the grouping selector
  // The endpoint logic will handle fetching municipality data
  return viewMode;
};

/**
 * Determines which endpoint to call based on grouping type and view mode
 * @param {string} effectiveViewMode - Effective view mode
 * @param {string} groupingType - Current grouping type
 * @param {string|null} activeCounty - Active county code
 * @param {string|null} activeUser - Active user
 * @param {string|null} activeMuni - Active municipality code
 * @returns {object|null} Endpoint configuration { endpoint, params } or null
 */
export const getGroupingEndpoint = (
  effectiveViewMode,
  groupingType,
  activeCounty,
  activeUser,
  activeMuni,
  myTasksUserId = null,
) => {
  const drillDownUserId = activeUser
    ? (typeof activeUser === 'string' ? activeUser : activeUser.id)
    : null;
  const assigneeUserId = drillDownUserId || myTasksUserId || null;
  const assigneeParams = assigneeUserId ? { userId: assigneeUserId } : {};

  if (effectiveViewMode === 'county') {
    if (groupingType === 'Municipality') {
      return {
        endpoint: '/load_task_counts_by_muni/all',
        params: assigneeParams,
      };
    }
    if (groupingType === 'User') {
      return {
        endpoint: '/load_task_counts_by_grouping/user',
        params: assigneeParams,
      };
    }
    return {
      endpoint: '/load_task_counts_by_grouping/county',
      params: assigneeParams,
    };
  }

  if (effectiveViewMode === 'municipality') {
    if (drillDownUserId) {
      return {
        endpoint: '/load_task_counts_by_muni/filtered',
        params: { userId: drillDownUserId },
      };
    }
    if (activeCounty?.code) {
      return {
        endpoint: `/load_task_counts_by_muni/${activeCounty.code}`,
        params: assigneeParams,
      };
    }
    if (groupingType === 'Municipality' && !activeCounty?.code) {
      return {
        endpoint: '/load_task_counts_by_muni/all',
        params: assigneeParams,
      };
    }
  } else if (effectiveViewMode === 'tasks' && activeMuni?.code) {
    const params = { ...assigneeParams };
    return {
      endpoint: `/load_tasks_by_muni/${activeMuni.code}`,
      params,
    };
  }
  
  return null;
};

/**
 * Transforms data based on grouping type
 * @param {Array} data - Raw data from backend
 * @param {string} groupingType - Current grouping type
 * @param {Function} getCountyName - Function to get county name from code
 * @param {Function} getLocationNameFromMuniCode - Function to get location name from MuniCode
 * @returns {Array} Transformed data
 */
export const transformGroupingData = (
  data,
  groupingType,
  getCountyName,
  getLocationNameFromMuniCode
) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((row, idx) => {
    const numeric = normalizeNumericFields(row, [
      { source: 'TotalParcels', target: 'totalTasks' },
      { source: 'CompletedCount', target: 'completedCount' },
      { source: 'DataCleaningCount', target: 'dataCleaningCount' },
      { source: 'CaseReviewCount', target: 'caseReviewCount' },
      { source: 'SettledCount', target: 'settledCount' },
    ]);

    const completedCount = numeric.completedCount || 0;
    const totalTasks = numeric.totalTasks || 0;
    const status = calculateStatus(completedCount, totalTasks);

    if (groupingType === 'County') {
      return {
        id: `county-${row.CountyCode}`,
        county: getCountyName(row.CountyCode),
        countyCode: row.CountyCode,
        ...numeric,
        status,
      };
    } else if (groupingType === 'Municipality') {
      const muniCode = row.MuniCode || row.muniCode || '';
      const idPrefix = row.id?.includes('muni-') ? row.id.split('-').slice(0, 2).join('-') : 'muni-all';
      return {
        id: `${idPrefix}-${idx}`,
        municipality: `${getLocationNameFromMuniCode?.(muniCode) || muniCode} (${muniCode || 'N/A'})`,
        muniCode: muniCode,
        ...numeric,
        status,
      };
    } else if (groupingType === 'User') {
      const userId = row.UserId || row.userId || row.Assignee || '';
      const userName = row.UserName || row.userName || row.Assignee || '';
      return {
        id: `user-${userId || idx}`,
        userId: userId,
        userName: userName,
        ...numeric,
        status,
      };
    }

    return { id: `unknown-${idx}`, ...numeric, status };
  });
};

/**
 * Normalizes numeric fields from backend response
 */
const normalizeNumericFields = (row, mappings) => {
  const normalized = {};
  mappings.forEach(({ source, target }) => {
    const value = row[source];
    normalized[target] = typeof value === 'number' ? value : parseInt(value || '0', 10);
  });
  return normalized;
};

/**
 * Calculates status based on completed and total counts
 */
const calculateStatus = (completedCount, totalTasks) => {
  if (totalTasks === 0) return 'No Tasks';
  if (completedCount === totalTasks) return 'Complete';
  if (completedCount === 0) return 'Not Started';
  return 'In Progress';
};

/**
 * Handles navigation click based on grouping type
 * @param {string} groupingType - Current grouping type
 * @param {object} row - Clicked row data
 * @param {object} stateSetters - Object containing all state setters
 */
export const handleGroupingNavigation = (groupingType, row, stateSetters) => {
  const {
    setActiveCounty,
    setActiveUser,
    setViewMode,
    setMuniCounts,
    setMuniTasks,
    setActiveMuni,
  } = stateSetters;

  if (groupingType === 'County') {
    setActiveCounty({ code: row.countyCode, name: row.county });
    setViewMode('municipality');
    setMuniCounts([]);
    setMuniTasks([]);
    setActiveMuni(null);
  } else if (groupingType === 'User') {
    const userId = row.userId || row.UserId || row.Assignee;
    const userName = row.userName || row.UserName || row.Assignee;
    setActiveUser({ id: userId, name: userName });
    setViewMode('municipality');
    setMuniCounts([]);
    setMuniTasks([]);
    setActiveMuni(null);
  }
};

/**
 * Clears grouping-specific state when switching grouping types
 * @param {string} newGroupingType - New grouping type being switched to
 * @param {object} stateSetters - Object containing all state setters
 */
export const clearGroupingState = (newGroupingType, stateSetters) => {
  const {
    setActiveCounty,
    setActiveUser,
    setActiveMuni,
  } = stateSetters;

  if (newGroupingType === 'Municipality') {
    setActiveCounty(null);
    setActiveMuni(null);
  } else if (newGroupingType === 'User') {
    setActiveUser(null);
    setActiveMuni(null);
    setActiveCounty(null);
  } else if (newGroupingType === 'County') {
    setActiveUser(null);
    setActiveMuni(null);
  }
};
